import { Box, Theme } from '@mui/material'
import { DataGridPremiumProps, GridCellSelectionModel, GridRowId } from '@mui/x-data-grid-premium'
import { RxChevronRight } from 'react-icons/rx'
import { If, Then } from 'react-if'
import { useUpdatedTableRowChildRowsSubscription } from '~/common/generated/apollo-operations'
import useStyles from '~/common/hooks/useStyles'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DetailedWorkspaceActions } from '~/components/renderers/workspaces/RenderWorkspace'
import { DetailedTable, DossTableRow, TableType } from '~/components/tables/types'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'

export interface TableProps extends Partial<DataGridPremiumProps> {
  actions: DetailedWorkspaceActions[]
  parentRowId?: string
  tableId: string
  showEmptyState: boolean
  showTitle?: boolean
  showPadding: boolean
  tableType: TableType
  cellSelectionModel?: GridCellSelectionModel
  handleCellSelectionModelChange?: (model: GridCellSelectionModel) => void
  showBorderBottom?: boolean
}

export type DossTableStyles = ReturnType<typeof getStyles>

function getStyles(theme: Theme) {
  return {
    // Styling for the Detail Panel Toggle
    detailPanelToggle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      fontSize: '14px',
      color: theme.palette.common.doss.gray[9],
      button: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transform(isExpanded?: boolean) {
          return {
            transform: `rotateZ(${isExpanded ? 90 : 0}deg)`,
          }
        },
      },
      '&:hover': {
        color: theme.palette.common.doss.gray[10],
        cursor: 'pointer',
      },
    },
  }
}

interface ChildTableDetailPanelToggleProps {
  id: GridRowId
  row: DossTableRow
  value: boolean
  table: DetailedTable
}

/**
 * Function for rendering the detail panel toggle button in the first column of the table.
 * This is the button that allows the user to expand the table to view the child table.
 */
export function ChildTableDetailPanelToggle({
  row,
  value: isExpanded,
  table,
}: ChildTableDetailPanelToggleProps) {
  const styles = useStyles(getStyles)

  const { t } = useTypedTranslation()

  useUpdatedTableRowChildRowsSubscription({
    variables: { tableRowId: row.id },
    skip: row.hasChildRows,
  })

  return (
    <If
      condition={
        row.hasChildRows ||
        table.columns
          .flatMap((column) => column.lookupColumnChildren)
          .filter((backlinkColumn) => backlinkColumn.lookupColumnGroup?.showBacklink).length > 0
      }
    >
      <Then>
        <Tooltip
          arrow
          disableInteractive
          leaveDelay={0}
          placement="bottom-start"
          title={t('table.view_child_table')}
        >
          <Box
            aria-label={isExpanded ? t('common.actions.close') : t('common.actions.open')}
            fontSize="inherit"
            sx={styles.detailPanelToggle}
          >
            <Box
              sx={{
                ...styles.detailPanelToggle.button,
                ...styles.detailPanelToggle.button.transform(isExpanded),
              }}
            >
              <RxChevronRight />
            </Box>
          </Box>
        </Tooltip>
      </Then>
    </If>
  )
}
